import React, { useState, useMemo } from 'react';
import { Card, DatePicker, Space, Tag, Button } from '@arco-design/web-react';
import { IconCalendar } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import ModuleHeader from '@/modules/common/components/layout/ModuleHeader';
import { useLlmCostSummary, useLlmCostDetail } from '../../hooks/useLlmCosts';
import LlmCostKpiCards from './LlmCostKpiCards';
import LlmCostTable from './LlmCostTable';

const { RangePicker } = DatePicker;

const formatDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LlmCostDashboard = () => {
  const { t } = useTranslation('finance');

  const DATE_PRESETS = useMemo(() => [
    {
      key: 'today',
      label: t('costs.presets.today'),
      getRange: () => {
        const today = new Date();
        return [formatDate(today), formatDate(today)];
      },
    },
    {
      key: 'thisWeek',
      label: t('costs.presets.thisWeek'),
      getRange: () => {
        const today = new Date();
        const monday = new Date(today);
        monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
        return [formatDate(monday), formatDate(today)];
      },
    },
    {
      key: 'thisMonth',
      label: t('costs.presets.thisMonth'),
      getRange: () => {
        const today = new Date();
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        return [formatDate(first), formatDate(today)];
      },
    },
    {
      key: 'lastMonth',
      label: t('costs.presets.lastMonth'),
      getRange: () => {
        const today = new Date();
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last = new Date(today.getFullYear(), today.getMonth(), 0);
        return [formatDate(first), formatDate(last)];
      },
    },
    {
      key: 'thisYear',
      label: t('costs.presets.thisYear'),
      getRange: () => {
        const today = new Date();
        const first = new Date(today.getFullYear(), 0, 1);
        return [formatDate(first), formatDate(today)];
      },
    },
  ], [t]);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateRange, setDateRange] = useState([
    formatDate(firstOfMonth),
    formatDate(today),
  ]);
  const [activePreset, setActivePreset] = useState('thisMonth');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const dateFrom = dateRange[0];
  const dateTo = dateRange[1];

  const { data: summary, isLoading: summaryLoading } = useLlmCostSummary(dateFrom, dateTo);
  const { data: detail, isLoading: detailLoading } = useLlmCostDetail({
    date_from: dateFrom,
    date_to: dateTo,
    page,
    page_size: pageSize,
  });

  const handleDateChange = (dateStrings) => {
    if (dateStrings && dateStrings[0] && dateStrings[1]) {
      setDateRange(dateStrings);
      setActivePreset(null);
      setPage(1);
    }
  };

  const handlePreset = (preset) => {
    const range = preset.getRange();
    setDateRange(range);
    setActivePreset(preset.key);
    setPage(1);
  };

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(1);
    }
  };

  return (
    <Card>
      <ModuleHeader
        title={t('costs.title')}
        inCard
        stats={
          summary?.pricing && (
            <Space size="mini">
              <Tag color="gray" size="small">
                Input: ${summary.pricing.input_per_mtok}/MTok
              </Tag>
              <Tag color="gray" size="small">
                Output: ${summary.pricing.output_per_mtok}/MTok
              </Tag>
            </Space>
          )
        }
        actions={
          <Space size="small">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.key}
                size="small"
                type={activePreset === preset.key ? 'primary' : 'outline'}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
            <RangePicker
              style={{ width: 240 }}
              size="small"
              format="YYYY-MM-DD"
              value={dateRange}
              onChange={handleDateChange}
              allowClear={false}
              prefix={<IconCalendar />}
            />
          </Space>
        }
      />

      <div style={{ marginTop: 16 }}>
        <LlmCostKpiCards summary={summary} loading={summaryLoading} />
      </div>

      <div style={{ marginTop: 16 }}>
        <LlmCostTable
          items={detail?.items}
          total={detail?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          loading={detailLoading}
          activeCallers={summary?.cost_by_caller?.map((c) => c.caller) || []}
        />
      </div>
    </Card>
  );
};

export default LlmCostDashboard;
